import React, { useState, useEffect } from 'react';
import { 
  Pencil, Trash2, Plus, Calendar, Clock, User, 
  FileText, X, ChevronLeft, ChevronRight, 
  Search, AlertCircle, Check
} from 'lucide-react';

const NoteSavedToast = ({ show }) => {
  if (!show) return null;
  
  return (
    <div className="fixed bottom-6 right-6 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center animate-slide-up z-50">
      <Check className="w-5 h-5 mr-3" />
      <span className="text-base">Note saved successfully</span>
    </div>
  );
};

const NoteDialog = ({ open, handleClose, note, handleSave, loading }) => {
  const [editedNote, setEditedNote] = useState(note);
  const [error, setError] = useState('');

  useEffect(() => {
    setEditedNote(note);
    setError('');
  }, [note]);

  const handleChange = (e) => {
    setEditedNote({ ...editedNote, [e.target.name]: e.target.value });
    setError('');
  };

  const validateAndSave = () => {
    if (!editedNote.notesText.trim()) {
      setError('Note content cannot be empty');
      return;
    }
    handleSave(editedNote);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-3xl mx-4 shadow-xl transform transition-all">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100">
          <h3 className="text-2xl font-semibold text-gray-900">
            {note.invoiceNotesId ? 'Edit Note' : 'Add New Note'}
          </h3>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 transition-colors p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-8">
          <div className="mb-6">
            <label htmlFor="notesText" className="block text-lg font-medium text-gray-700 mb-2">
              Note Content
            </label>
            <textarea
              id="notesText"
              name="notesText"
              rows="6"
              className={`w-full px-6 py-4 rounded-lg border ${error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} 
                focus:border-transparent focus:ring-2 transition-shadow text-gray-900 text-lg`}
              value={editedNote.notesText || ''}
              onChange={handleChange}
              placeholder="Enter your note content here..."
            />
            {error && (
              <div className="mt-3 flex items-center text-base text-red-600">
                <AlertCircle className="w-5 h-5 mr-2" />
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end items-center gap-4 px-8 py-6 bg-gray-50 rounded-b-xl">
          <button
            onClick={handleClose}
            className="px-6 py-3 text-lg font-medium text-gray-700 bg-white border border-gray-300 rounded-lg 
              hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={validateAndSave}
            className="px-6 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Saving...' : (note.invoiceNotesId ? 'Save Changes' : 'Add Note')}
          </button>
        </div>
      </div>
    </div>
  );
};

const TableHeader = ({ children, icon: Icon }) => (
  <th className="px-8 py-4 text-left">
    <div className="flex items-center gap-3">
      <Icon className="w-5 h-5 text-gray-400" />
      <span className="text-base font-medium text-gray-500 uppercase tracking-wider">{children}</span>
    </div>
  </th>
);

const SearchInput = ({ value, onChange }) => (
  <div className="relative">
    <Search className="w-6 h-6 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder="Search notes..."
      className="pl-12 pr-6 py-3 w-full text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  </div>
);

const SystemNotesTable = ({ notesData, invoiceId, saveNotes, authInfo }) => {
  const [notes, setNotes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  
  const getUserId = () => {
    return authInfo && authInfo.userId ? authInfo.userId : '';
  };
  
  const [currentNote, setCurrentNote] = useState({
    userId: getUserId(),
    createdDate: new Date().toISOString().split('T')[0],
    createdTime: new Date().toTimeString().split(' ')[0],
    idCompany: 1,
    invoiceId: invoiceId,
    invoiceNotesId: null,
    notesIndicator: 2,
    notesText: '',
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (notesData && Array.isArray(notesData)) {
      setNotes(notesData);
    }
  }, [notesData]);

  const handleSaveNote = async (note) => {
    setLoading(true);
    try {
      const noteToSave = {
        ...note,
        userId: getUserId() || note.userId,
        invoiceId: invoiceId
      };
      
      await saveNotes(noteToSave);
      setNoteDialogOpen(false);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      
      // Reset the current note for next addition
      setCurrentNote({
        userId: getUserId(),
        createdDate: new Date().toISOString().split('T')[0],
        createdTime: new Date().toTimeString().split(' ')[0],
        idCompany: 1,
        invoiceId: invoiceId,
        invoiceNotesId: null,
        notesIndicator: 2,
        notesText: '',
      });
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredNotes = notes.filter(note => 
    (note.notesText && note.notesText.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (note.userId && note.userId.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredNotes.length / rowsPerPage);
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentNotes = filteredNotes.slice(startIndex, endIndex);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      return timeString.substring(0, 5); // Show only HH:MM
    } catch (e) {
      return timeString;
    }
  };

  const handleAddNote = () => {
    setCurrentNote({
      userId: getUserId(),
      createdDate: new Date().toISOString().split('T')[0],
      createdTime: new Date().toTimeString().split(' ')[0],
      idCompany: 1,
      invoiceId: invoiceId,
      invoiceNotesId: null,
      notesIndicator: 2,
      notesText: '',
    });
    setNoteDialogOpen(true);
  };

  const handleEditNote = (note) => {
    setCurrentNote({
      ...note,
      userId: note.userId || getUserId(),
    });
    setNoteDialogOpen(true);
  };

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <SearchInput value={searchTerm} onChange={handleSearch} />
        <button
          onClick={handleAddNote}
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white text-lg rounded-lg hover:bg-blue-700 
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          <Plus className="w-5 h-5 mr-3" />
          Add Note
        </button>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader icon={User}>User</TableHeader>
                <TableHeader icon={Calendar}>Date</TableHeader>
                <TableHeader icon={Clock}>Time</TableHeader>
                <TableHeader icon={FileText}>Note Content</TableHeader>
                <th className="px-8 py-4 text-right">
                  <span className="text-base font-medium text-gray-500 uppercase">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {currentNotes.map((note, index) => (
                <tr 
                  key={note.invoiceNotesId || index}
                  className="hover:bg-gray-50 transition-colors duration-150"
                >
                  <td className="px-8 py-6 whitespace-nowrap text-base font-medium text-gray-900">
                    {note.userId || 'N/A'}
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-base text-gray-500">
                    {formatDate(note.createdDate)}
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-base text-gray-500">
                    {formatTime(note.createdTime)}
                  </td>
                  <td className="px-8 py-6 text-base text-gray-900">
                    <div className="max-w-xl truncate" title={note.notesText}>
                      {note.notesText || 'N/A'}
                    </div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => handleEditNote(note)}
                        className="p-3 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit note"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {currentNotes.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-8 py-12 text-center text-gray-500 text-lg">
                    {searchTerm ? 'No notes match your search' : 'No notes available for this invoice'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredNotes.length > 0 && (
          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-base text-gray-700">Show</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  className="text-base border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 px-3 py-2"
                >
                  {[5, 10, 25, 50].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="text-base text-gray-700">entries</span>
              </div>

              <div className="flex items-center gap-6">
                <span className="text-base text-gray-700">
                  Page {page + 1} of {Math.max(1, totalPages)}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="p-3 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="p-3 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <NoteDialog
        open={noteDialogOpen}
        handleClose={() => setNoteDialogOpen(false)}
        note={currentNote}
        handleSave={handleSaveNote}
        loading={loading}
      />

      <NoteSavedToast show={showToast} />
    </div>
  );
};

export default SystemNotesTable;